/**
 * The platform's one severity scale, in the UI. Generated from the auth SDK's
 * catalog/severity.yaml (scripts/gen-severity.js); the services grade with the same file
 * through saf3ai_auth.severity, so a level means the same thing on every module and surface.
 *
 *   import { normalize, label, color, fromScore, SEVERITY_LEVELS } from '../../severity';
 *
 *   normalize('Critical')      // 'critical'
 *   normalize('warning')       // 'medium'
 *   normalize(undefined)       // 'unknown' - never a level
 *   fromScore(65)              // 'high'   (0-100 risk score)
 *   health(82)                 // { status: 'fair', label: 'Partially compliant' }
 *   color('low')               // blue: green means good / clean only
 *
 * Pages never keep their own colour map, label table or score cut-offs: the severity ratchet
 * (npm run lint:severity) fails a file that adds one.
 */
import DEF from './severity.generated';

export const SEVERITY_DEFINITION = DEF;
/** Worst first: critical, high, medium, low, info. */
export const SEVERITY_LEVELS = Object.freeze(Object.keys(DEF.levels));
/** The levels that are findings (info is a fact, not a finding). */
export const FINDING_LEVELS = Object.freeze(SEVERITY_LEVELS.filter((lv) => lv !== 'info'));
export const UNKNOWN = 'unknown';
export const NONE = 'none';

const key = (text) => String(text ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

const DETECTION_INDEX = (() => {
  const out = {};
  ['critical', 'high', 'medium', 'low'].forEach((lv) => {
    (DEF.detections[lv] || []).forEach((cat) => { out[key(cat)] = lv; });
  });
  (DEF.detections.by_data_class || []).forEach((cat) => { out[key(cat)] = 'by_data_class'; });
  return out;
})();
const CONFIRMABLE = new Set((DEF.confirmable || []).map(key));
const DATA_CLASS_INDEX = (() => {
  const out = {};
  Object.entries(DEF.data_classes).forEach(([lv, types]) => types.forEach((t) => { out[key(t)] = lv; }));
  return out;
})();

/**
 * Any producer's severity onto a level or a state: case-insensitive, aliases (informational ->
 * info, warning -> medium, error -> high), 1-5 numerals. Anything else, including a missing
 * value, is 'unknown'.
 */
export function normalize(value) {
  if (value == null) return UNKNOWN;
  const text = String(value).trim().toLowerCase();
  if (DEF.levels[text] || DEF.states[text]) return text;
  if (Object.prototype.hasOwnProperty.call(DEF.aliases, text)) return DEF.aliases[text];
  if (Object.prototype.hasOwnProperty.call(DEF.numeric_1_5, text)) return DEF.numeric_1_5[text];
  return UNKNOWN;
}

/** critical 4 .. info 0; none -1; unknown -2 (sorts below every level). */
export function rank(value) {
  const lv = normalize(value);
  if (DEF.levels[lv]) return DEF.levels[lv].rank;
  return lv === NONE ? -1 : -2;
}

/** For Array.prototype.sort: worst first. */
export const byWorst = (a, b) => rank(b) - rank(a);

export const isLevel = (value) => Boolean(DEF.levels[normalize(value)]);
export const isFinding = (value) => FINDING_LEVELS.includes(normalize(value));

/** Display label: 'Critical' ... 'Info', 'None', 'Unknown'. */
export function label(value) {
  const lv = normalize(value);
  return (DEF.levels[lv] || DEF.states[lv] || {}).label || 'Unknown';
}

/** What the level means and the response it asks for (for tooltips and legends). */
export function meaning(value) {
  const lv = normalize(value);
  const entry = DEF.levels[lv] || DEF.states[lv] || {};
  return { meaning: entry.meaning || '', response: entry.response || '' };
}

export const color = (value) => DEF.colors[normalize(value)];

/** The highest level among values; 'none' when every value is none; 'unknown' when nothing was graded. */
export function worst(values) {
  let best = null;
  let seenNone = false;
  (values || []).forEach((v) => {
    const lv = normalize(v);
    if (DEF.levels[lv]) {
      if (best == null || rank(lv) > rank(best)) best = lv;
    } else if (lv === NONE) {
      seenNone = true;
    }
  });
  return best || (seenNone ? NONE : UNKNOWN);
}

const toNumber = (score) => {
  if (score == null || score === '' || typeof score === 'boolean') return null;
  const n = Number(score);
  return Number.isFinite(n) ? n : null;
};

function band(score, bands, field = 'level') {
  const hit = bands.find((b) => score >= b.min);
  return hit ? hit[field] : null;
}

/** A 0-100 risk score (higher is worse): >=80 critical, >=60 high, >=40 medium, >0 low, 0 none. */
export function fromScore(score) {
  const n = toNumber(score);
  if (n == null) return UNKNOWN;
  return band(n, DEF.risk_score_bands) || NONE;
}

export function fromCvss(score) {
  const n = toNumber(score);
  if (n == null) return UNKNOWN;
  return band(n, DEF.cvss_bands) || NONE;
}

/**
 * A health score (higher is better) onto its status: compliance 90 / 70, posture 80 / 60 / 40.
 * null when there is no score.
 */
export function health(score, kind = 'compliance') {
  const n = toNumber(score);
  if (n == null) return null;
  const hit = (DEF.health_bands[kind] || []).find((b) => n >= b.min);
  return hit ? { status: hit.status, label: hit.label } : null;
}

/** Sensitive data by class; an entity type the scale does not list is high (personal data). */
export const fromDataClass = (entityType) => DATA_CLASS_INDEX[key(entityType)] || 'high';

/**
 * A detection category's level, the same in every scanner vocabulary. `confirmed` lifts a
 * jailbreak / injection to critical. Sensitive data takes its data class. An unlisted category
 * is 'unknown'.
 */
export function fromDetection(category, { confirmed = false, dataClass = null, dataClasses = [] } = {}) {
  const k = key(category);
  const lv = DETECTION_INDEX[k];
  if (lv === 'by_data_class') {
    const classes = [dataClass, ...dataClasses].filter(Boolean);
    return classes.length ? worst(classes.map(fromDataClass)) : 'high';
  }
  if (!lv) return UNKNOWN;
  if (confirmed && CONFIRMABLE.has(k)) return 'critical';
  return lv;
}

/** Counts by level, worst first, with every level present: { critical: 0, high: 2, ... }. */
export function emptyCounts(levels = FINDING_LEVELS) {
  return levels.reduce((acc, lv) => ({ ...acc, [lv]: 0 }), {});
}

export { default as SeverityChip } from './SeverityChip';
